import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, ShieldAlert } from 'lucide-react';
import { customerSsoService, CustomerSsoError } from '@/services/customer/customer-sso.service';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { analyticsService } from '@/services/analytics.service';
import { Button } from '@/components/ui/button';
import { authService } from '@/services/auth.service';
import { logger } from '@/lib/logger';
import { resolveSigninRedirectUrl } from '@/lib/auth/signin-redirect';

type SsoState = 'loading' | 'expired' | 'consumed' | 'invalid' | 'error';

export default function CustomerSsoConsumer() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hydrateFromResponse, isAuthenticated: isCustomerAuthenticated } = useCustomerAuth();
  const [state, setState] = useState<SsoState>('loading');
  const [error, setError] = useState<string | null>(null);
  const tokenRef = useRef<string | null>(null);
  const redirectRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const bypassRef = useRef(false);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const scrubQueryParams = useCallback((replacement?: string) => {
    if (typeof window === 'undefined') {
      return;
    }

    if (replacement) {
      try {
        const normalized = new URL(replacement, window.location.href);
        if (normalized.origin === window.location.origin) {
          window.history.replaceState({}, '', `${normalized.pathname}${normalized.search}${normalized.hash}`);
          return;
        }
      } catch (scrubError) {
        logger.warn('[customer-sso] Unable to replace history with redirect target', scrubError);
      }
    }

    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('token');
      url.searchParams.delete('redirect');
      const cleanedQuery = url.searchParams.toString();
      const next = `${url.pathname}${cleanedQuery ? `?${cleanedQuery}` : ''}${url.hash}`;
      window.history.replaceState({}, '', next);
    } catch (scrubError) {
      logger.warn('[customer-sso] Failed to scrub SSO query params', scrubError);
    }
  }, []);

  const notifyProviderPortal = useCallback((reason: 'expired' | 'consumed') => {
    if (typeof window === 'undefined') {
      return;
    }

    const payload = {
      type: 'florentin:customer-sso:request-new-link',
      reason,
      timestamp: Date.now(),
      host: window.location.host,
    };

    try {
      window.postMessage(payload, '*');
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(payload, '*');
      }
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(payload, '*');
      }
    } catch (postMessageError) {
      logger.warn('[customer-sso] Failed to notify provider portal about expired token', postMessageError);
    }

    analyticsService.track('customer_portal_sso_request_new_link', { reason });
  }, []);

  const attemptSsoExchange = useCallback(async () => {
    if (bypassRef.current) {
      return;
    }

    const params = new URLSearchParams(search);

    if (!tokenRef.current) {
      tokenRef.current = params.get('token');
    }

    if (!redirectRef.current) {
      redirectRef.current = params.get('redirect');
    }

    const token = tokenRef.current;

    if (!token) {
      setError(t('customerSso.missingToken', 'Missing or invalid magic link token.'));
      setState('error');
      scrubQueryParams();
      return;
    }

    analyticsService.track('customer_portal_sso_exchange_attempt', {
      hasToken: true,
    });

    try {
      const result = await customerSsoService.consume(token);
      const payload = result.payload;

      if (!payload.token) {
        throw new Error('SSO exchange succeeded without a bearer token.');
      }

      const tokenType =
        (payload as { token_type?: string | null; tokenType?: string | null }).token_type ??
        (payload as { tokenType?: string | null }).tokenType ??
        undefined;

      authService.persistCustomerToken(payload.token, tokenType);
      await hydrateFromResponse(result.payload);
      try {
        await authService.getCurrentCustomer();
      } catch (confirmError) {
        logger.warn('[customer-sso] Customer confirmation failed after SSO exchange', confirmError);
      }
      analyticsService.track('customer_portal_sso_consumed', {
        providerId: result.payload.provider.id,
        userId: result.payload.user.id,
      });

      const signinRedirectUrl = resolveSigninRedirectUrl(result.payload);
      const finalRedirect = signinRedirectUrl ?? result.redirectUrl ?? redirectRef.current ?? '/customer';
      scrubQueryParams(finalRedirect);

      try {
        const normalized = new URL(finalRedirect, window.location.origin);
        const sameOrigin = normalized.origin === window.location.origin;
        const targetPath = `${normalized.pathname}${normalized.search}${normalized.hash}`;

        if (sameOrigin) {
          navigate(targetPath, { replace: true });
        } else {
          window.location.replace(normalized.toString());
        }
      } catch {
        window.location.replace(finalRedirect);
      }
    } catch (exchangeError: unknown) {
      if (!mountedRef.current) {
        return;
      }

      if (exchangeError instanceof CustomerSsoError) {
        analyticsService.track('customer_portal_sso_failed', {
          code: exchangeError.code,
          status: exchangeError.status ?? null,
        });

        logger.error('[customer-sso] Exchange failed', exchangeError);

        if (exchangeError.code === 'expired') {
          if (isCustomerAuthenticated) {
            scrubQueryParams('/customer');
            navigate('/customer', { replace: true });
            return;
          }
          setError(
            exchangeError.message ??
              t('customerSso.expiredMessage', 'This sign-in link expired. Request a fresh link to continue.')
          );
          setState('expired');
          scrubQueryParams();
          return;
        }

        if (exchangeError.code === 'consumed') {
          if (isCustomerAuthenticated) {
            scrubQueryParams('/customer');
            navigate('/customer', { replace: true });
            return;
          }

          setError(
            t('customerSso.consumedMessage', 'Sign in with a fresh SSO link or return to the login screen to continue with email.')
          );
          setState('consumed');
          scrubQueryParams();
          return;
        }

        if (exchangeError.code === 'invalid') {
          setError(
            t('customerSso.invalidMessage', 'This sign-in link is invalid. Request a new link to continue.')
          );
          setState('invalid');
          scrubQueryParams();
          return;
        }
      } else {
        analyticsService.track('customer_portal_sso_failed', {
          code: 'unexpected',
        });
        logger.error('[customer-sso] Unexpected error during exchange', exchangeError);
      }

      const fallbackMessage =
        exchangeError instanceof Error
          ? exchangeError.message
          : t('customerSso.exchangeFailed', 'Unable to complete the magic link exchange.');
      setError(fallbackMessage);
      setState('error');
      scrubQueryParams();
    }
  }, [hydrateFromResponse, isCustomerAuthenticated, navigate, scrubQueryParams, search, t]);

  const handleRetry = useCallback(() => {
    setState('loading');
    setError(null);
    void attemptSsoExchange();
  }, [attemptSsoExchange]);

  useEffect(() => {
    if (typeof window === 'undefined' || bypassRef.current) {
      return;
    }

    if (!isCustomerAuthenticated) {
      return;
    }

    const params = new URLSearchParams(search);
    const redirect = params.get('redirect');
    const fallback = redirect ?? '/customer';
    bypassRef.current = true;
    scrubQueryParams(fallback);

    try {
      const normalized = new URL(fallback, window.location.origin);
      const sameOrigin = normalized.origin === window.location.origin;
      const targetPath = `${normalized.pathname}${normalized.search}${normalized.hash}`;

      if (sameOrigin) {
        navigate(targetPath, { replace: true });
      } else {
        window.location.replace(normalized.toString());
      }
    } catch {
      window.location.replace(fallback);
    }
  }, [isCustomerAuthenticated, navigate, scrubQueryParams, search]);

  useEffect(() => {
    void attemptSsoExchange();
  }, [attemptSsoExchange]);

  if (state === 'loading') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-4 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <div>
          <p className="text-lg font-semibold">
            {t('customerSso.loadingTitle', 'Restoring your session…')}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('customerSso.loadingMessage', 'Hang tight while we sign you into the customer portal.')}
          </p>
        </div>
      </div>
    );
  }

  const renderExpired = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <p className="text-lg font-semibold">
          {t('customerSso.expiredTitle', 'This sign-in link expired')}
        </p>
        <p className="text-sm text-muted-foreground">
          {error ??
            t(
              'customerSso.expiredMessage',
              'Ask your provider for a new SSO link or bump them below and we will let them know automatically.'
            )}
        </p>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row">
        <Button className="flex-1" onClick={() => notifyProviderPortal('expired')}>
          {t('customerSso.requestNewLink', 'Request new SSO link')}
        </Button>
        <Button className="flex-1" onClick={() => navigate('/customer/login')} variant="outline">
          {t('customerSso.backToLogin', 'Back to login')}
        </Button>
      </div>
    </div>
  );

  const renderConsumed = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <p className="text-lg font-semibold">
          {t('customerSso.consumedTitle', 'This link was already used')}
        </p>
        <p className="text-sm text-muted-foreground">
          {error ??
            t(
              'customerSso.consumedMessage',
              'Sign in with a fresh SSO link or return to the login screen to continue with email.'
            )}
        </p>
      </div>
      <Button className="w-full" onClick={() => navigate('/customer/login')}>
        {t('customerSso.backToLogin', 'Back to login')}
      </Button>
    </div>
  );

  const renderInvalid = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <p className="text-lg font-semibold">
          {t('customerSso.invalidTitle', 'This sign-in link is invalid')}
        </p>
        <p className="text-sm text-muted-foreground">
          {error ??
            t('customerSso.invalidMessage', 'Request a new portal link from your provider to continue.')}
        </p>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row">
        <Button className="flex-1" onClick={() => notifyProviderPortal('expired')}>
          {t('customerSso.requestNewLink', 'Request new SSO link')}
        </Button>
        <Button className="flex-1" onClick={() => navigate('/customer/login')} variant="outline">
          {t('customerSso.backToLogin', 'Back to login')}
        </Button>
      </div>
    </div>
  );

  const renderGenericError = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <p className="text-lg font-semibold">
          {t('customerSso.errorTitle', 'Magic link expired or invalid')}
        </p>
        <p className="text-sm text-muted-foreground">
          {error ??
            t(
              'customerSso.errorMessage',
              'Request a new portal link from your provider or sign in with your email to continue.',
            )}
        </p>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row">
        <Button className="flex-1" onClick={handleRetry}>
          {t('customerSso.retry', 'Try again')}
        </Button>
        <Button className="flex-1" onClick={() => navigate('/customer/login')} variant="outline">
          {t('customerSso.backToLogin', 'Back to login')}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-background px-4 text-center">
      <ShieldAlert className="h-10 w-10 text-destructive" />
      {state === 'expired' ? renderExpired() : null}
      {state === 'consumed' ? renderConsumed() : null}
      {state === 'invalid' ? renderInvalid() : null}
      {state === 'error' ? renderGenericError() : null}
    </div>
  );
}
